import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Search,
  ArrowUpDown,
  Phone,
  Mail,
  ExternalLink,
  RefreshCw,
  Users,
  Sparkles,
  MessageCircle,
  CheckCircle2,
  XCircle,
  Award,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { LOGO_URL, BRAND } from "@/pwa/data";
import { adminApi, clearToken, STATUS_OPTIONS, statusMeta } from "./auth";
import LeadDrawer from "./LeadDrawer";

const fmtDate = (v) => {
  try {
    const d = typeof v === "string" ? new Date(v) : v;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_e) {
    return String(v);
  }
};

const STAT_ICONS = {
  total: Users,
  new: Sparkles,
  contacted: MessageCircle,
  quote_sent: ArrowUpDown,
  booked: CheckCircle2,
  completed: Award,
  lost: XCircle,
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortDesc, setSortDesc] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/quotes"),
      ]);
      setStats(s.data);
      setLeads(l.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        nav("/admin", { replace: true });
        return;
      }
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const logout = () => {
    clearToken();
    toast.success("Signed out");
    nav("/admin", { replace: true });
  };

  const onLeadUpdated = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setSelected(updated);
    // refresh stats after status change
    adminApi.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    let out = leads;
    if (statusFilter !== "all") out = out.filter((l) => l.status === statusFilter);
    if (qq) {
      out = out.filter((l) =>
        [l.name, l.email, l.phone, l.project_type, l.project_details, l.notes]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(qq))
      );
    }
    out = [...out].sort((a, b) => {
      const t1 = new Date(a.created_at).getTime();
      const t2 = new Date(b.created_at).getTime();
      return sortDesc ? t2 - t1 : t1 - t2;
    });
    return out;
  }, [leads, q, statusFilter, sortDesc]);

  const statCards = [
    { k: "total", label: "Total Leads", value: stats?.total ?? 0 },
    { k: "new", label: "New", value: stats?.new ?? 0 },
    { k: "contacted", label: "Contacted", value: stats?.contacted ?? 0 },
    { k: "quote_sent", label: "Quote Sent", value: stats?.quote_sent ?? 0 },
    { k: "booked", label: "Booked", value: stats?.booked ?? 0 },
    { k: "completed", label: "Completed", value: stats?.completed ?? 0 },
    { k: "lost", label: "Lost", value: stats?.lost ?? 0 },
  ];

  return (
    <div
      data-testid={ADMIN.dashboard}
      className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A]"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#121212] text-[#FAF9F6] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3 group" aria-label="Home">
            <span className="block w-10 h-10 rounded-full overflow-hidden ring-1 ring-[#C19A6B]/40 bg-[#FAF9F6] shrink-0">
              <img src={LOGO_URL} alt="Precision" className="w-full h-full object-cover" />
            </span>
            <div className="leading-tight hidden sm:block">
              <p className="font-serif-display text-lg">Precision · Admin</p>
              <p className="text-[9px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold">
                Lead CRM · {BRAND.location}
              </p>
            </div>
          </a>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={loadAll}
              className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#FAF9F6]/70 hover:text-[#D9B27F] transition-colors px-3 py-2 border border-white/10 hover:border-[#C19A6B]"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid={ADMIN.logoutButton}
              onClick={logout}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase bg-[#C19A6B] text-[#1A1A1A] hover:bg-[#D9B27F] transition-colors px-3 py-2 font-semibold"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-10">
        {/* Greeting */}
        <div className="mb-10">
          <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C4033] font-semibold">
            — Dashboard
          </p>
          <h1 className="mt-3 font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1A1A1A]">
            Welcome back, <span className="italic text-[#5C4033]">Nikki.</span>
          </h1>
          <p className="mt-3 text-[#1A1A1A]/65 max-w-2xl">
            Here's what's happening across your incoming projects. Click a lead to view details,
            update status, and add notes.
          </p>
        </div>

        {/* Stat cards */}
        <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4 mb-10">
          {statCards.map((c) => {
            const Icon = STAT_ICONS[c.k] || Users;
            return (
              <div
                key={c.k}
                data-testid={ADMIN.statCard(c.k)}
                className="bg-white border border-[#1A1A1A]/10 p-5 hover:border-[#5C4033] transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="w-8 h-8 bg-[#F1ECE0] flex items-center justify-center text-[#5C4033]">
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="text-[9px] tracking-[0.28em] uppercase text-[#1A1A1A]/40 font-semibold">
                    {c.k === "total" ? "All" : c.k.replace("_", " ")}
                  </span>
                </div>
                <p className="font-serif-display text-4xl text-[#1A1A1A] leading-none">
                  {c.value}
                </p>
                <p className="mt-2 text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/55 font-semibold">
                  {c.label}
                </p>
              </div>
            );
          })}
        </section>

        {/* Pipeline pills */}
        <section className="mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter("all")}
              data-testid="admin-filter-all"
              className={`px-4 py-2 text-[11px] tracking-[0.24em] uppercase font-semibold border transition-all ${
                statusFilter === "all"
                  ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]"
                  : "bg-transparent text-[#1A1A1A]/70 border-[#1A1A1A]/20 hover:border-[#1A1A1A]"
              }`}
            >
              All ({leads.length})
            </button>
            {STATUS_OPTIONS.map((opt) => {
              const count = leads.filter((l) => l.status === opt.value).length;
              const active = statusFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`${ADMIN.filterStatus}-${opt.value}`}
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-4 py-2 text-[11px] tracking-[0.24em] uppercase font-semibold border transition-all ${
                    active
                      ? `${opt.color} border-transparent`
                      : "bg-transparent text-[#1A1A1A]/70 border-[#1A1A1A]/20 hover:border-[#1A1A1A]"
                  }`}
                >
                  {opt.label} ({count})
                </button>
              );
            })}
          </div>
        </section>

        {/* Search & sort */}
        <section className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#1A1A1A]/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid={ADMIN.searchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone, project…"
              className="w-full pl-10 pr-4 py-3 border border-[#1A1A1A]/15 bg-white text-sm text-[#1A1A1A] placeholder-[#1A1A1A]/40 focus:outline-none focus:border-[#5C4033] transition-colors"
            />
          </div>
          <button
            data-testid={ADMIN.sortToggle}
            onClick={() => setSortDesc((v) => !v)}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 text-[11px] tracking-[0.24em] uppercase font-semibold border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors"
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            {sortDesc ? "Newest First" : "Oldest First"}
          </button>
        </section>

        {/* Leads */}
        <section>
          {loading ? (
            <div className="py-24 flex items-center justify-center text-[#1A1A1A]/60">
              <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading leads…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center border border-dashed border-[#1A1A1A]/15 bg-white">
              <p className="font-serif-display text-2xl text-[#1A1A1A]">No leads yet.</p>
              <p className="mt-2 text-[#1A1A1A]/60 text-sm">
                When someone submits the quote form, they'll show up here.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block bg-white border border-[#1A1A1A]/10 overflow-hidden">
                <table
                  data-testid={ADMIN.leadsTable}
                  className="w-full text-sm"
                >
                  <thead className="bg-[#F1ECE0] text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/70 font-semibold">
                    <tr>
                      <th className="text-left px-5 py-4">Name</th>
                      <th className="text-left px-5 py-4">Project</th>
                      <th className="text-left px-5 py-4">Contact</th>
                      <th className="text-left px-5 py-4">Budget</th>
                      <th className="text-left px-5 py-4">Status</th>
                      <th className="text-left px-5 py-4">Submitted</th>
                      <th className="px-5 py-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => {
                      const meta = statusMeta(l.status);
                      return (
                        <tr
                          key={l.id}
                          data-testid={ADMIN.leadRow(l.id)}
                          className="border-t border-[#1A1A1A]/8 hover:bg-[#F1ECE0]/50 cursor-pointer transition-colors"
                          onClick={() => setSelected(l)}
                        >
                          <td className="px-5 py-4">
                            <p className="font-medium text-[#1A1A1A]">{l.name}</p>
                            <p className="text-xs text-[#1A1A1A]/50">{l.timeline}</p>
                          </td>
                          <td className="px-5 py-4">
                            <p className="text-[#1A1A1A]">{l.project_type}</p>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex flex-col gap-1">
                              <a
                                href={`tel:${l.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-[#5C4033] hover:text-[#1A1A1A] inline-flex items-center gap-1.5 text-xs"
                              >
                                <Phone className="w-3 h-3" />
                                {l.phone}
                              </a>
                              <a
                                href={`mailto:${l.email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-[#5C4033] hover:text-[#1A1A1A] inline-flex items-center gap-1.5 text-xs truncate max-w-[220px]"
                              >
                                <Mail className="w-3 h-3" />
                                {l.email}
                              </a>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-[#1A1A1A]/85">{l.budget_range}</td>
                          <td className="px-5 py-4">
                            <span className={`inline-block px-2.5 py-1 text-[9px] tracking-[0.24em] uppercase font-semibold ${meta.color}`}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-[#1A1A1A]/60 whitespace-nowrap">
                            {fmtDate(l.created_at)}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <button
                              data-testid={ADMIN.leadOpen(l.id)}
                              className="inline-flex items-center gap-1 text-[#5C4033] hover:text-[#1A1A1A] text-xs tracking-widest uppercase font-semibold"
                            >
                              Open <ExternalLink className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {filtered.map((l) => {
                  const meta = statusMeta(l.status);
                  return (
                    <button
                      key={l.id}
                      data-testid={ADMIN.leadRow(l.id)}
                      onClick={() => setSelected(l)}
                      className="w-full text-left bg-white border border-[#1A1A1A]/10 p-4 hover:border-[#5C4033] transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-[#1A1A1A] truncate">{l.name}</p>
                          <p className="text-xs text-[#1A1A1A]/55 mt-1">{l.project_type}</p>
                        </div>
                        <span className={`inline-block px-2 py-1 text-[9px] tracking-[0.24em] uppercase font-semibold ${meta.color} shrink-0`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-[#1A1A1A]/8 flex items-center justify-between text-xs text-[#1A1A1A]/60">
                        <span>{l.budget_range}</span>
                        <span>{fmtDate(l.created_at)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* Recent inquiries strip */}
        {stats?.recent?.length > 0 && (
          <section className="mt-12">
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C4033] font-semibold mb-4">
              — Recent Inquiries
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.recent.slice(0, 6).map((l) => {
                const meta = statusMeta(l.status);
                return (
                  <button
                    key={l.id}
                    onClick={() => setSelected(l)}
                    className="text-left bg-white border border-[#1A1A1A]/10 p-4 hover:border-[#5C4033] transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-[#1A1A1A] truncate">{l.name}</p>
                      <span className={`inline-block px-2 py-0.5 text-[9px] tracking-[0.24em] uppercase font-semibold ${meta.color}`}>
                        {meta.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#1A1A1A]/60 truncate">{l.project_type}</p>
                    <p className="mt-2 text-[10px] text-[#1A1A1A]/45">{fmtDate(l.created_at)}</p>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>

      <LeadDrawer
        lead={selected}
        onClose={() => setSelected(null)}
        onUpdated={onLeadUpdated}
      />
    </div>
  );
};

export default AdminDashboard;
